// B5 (Launch-Batch-2, 2026-06-10) — SSO-Hint-Protokoll + Entscheidungslogik.
//
// Geschichte: v1 des Handoffs (5c28e56) kopierte den oidc-userString des
// PORTAL-Clients (scrolly-admin) per postMessage in den Viewer-sessionStorage
// und reloadete. Zwei strukturelle Probleme:
//   1. Der iframe-Pfad loopte (onLoad → send → storage-write + reload →
//      onLoad → …, Revert 4ff204d „Zucken").
//   2. Das kopierte Token gehörte zum falschen Client: accessTokenLifespan
//      ist 300s, und der Renew mit client_id=scrolly-public gegen ein
//      scrolly-admin-Refresh-Token wäre an Keycloak gescheitert →
//      nach ≤5 Minuten wieder anonym (B6-Symptomatik zurück).
//
// v2 (dieses Modul): Das Portal sendet nur noch einen HINT („es existiert
// eine Keycloak-SSO-Session"). Der Viewer macht daraufhin EINMAL einen
// top-level signinRedirect (prompt=none) mit seinem EIGENEN Client
// scrolly-public → echtes Token inkl. Refresh, automaticSilentRenew läuft
// über den Token-Endpoint (kein iframe → kein Safari-ITP-Problem).
// Kein storage-write fremder Tokens, kein reload-on-message — der
// 29.05.-Loop-Motor existiert in v2 nicht mehr.
//
// Sync-Hinweis: Message-Typen sind dupliziert in
// portal/src/lib/sso-handoff.ts (eigene Workspaces, kein Shared-Package).
// Drift-Lock: sso-hint.test.ts pinnt die Literale.

/** Message-Typ Portal → Viewer. */
export const SSO_HANDOFF_MESSAGE_TYPE = "scrolly-sso-handoff";
/** Ack Viewer → Portal (stoppt das Sender-Polling). */
export const SSO_ACK_MESSAGE_TYPE = "scrolly-sso-ack";

/**
 * sessionStorage-Flag: in diesem Tab wurde der Hint-Redirect bereits
 * versucht. sessionStorage ist per-Tab und überlebt den Redirect-
 * Roundtrip (view → auth.scrolly.com → view) — perfekte
 * once-Semantik gegen jede Wiederholungs-Schleife.
 */
export const SSO_ATTEMPTED_KEY = "scrolly:sso-hint-attempted";

/**
 * sessionStorage: return_to für den Silent-Fail-Pfad. Primär reist
 * return_to im OIDC url_state; bei prompt=none-Fehlern (login_required)
 * ist der Zugriff auf url_state lib-abhängig — dieser Key ist der
 * robuste Fallback für oidc-callback/+Page.tsx.
 */
export const SSO_RETURN_TO_KEY = "scrolly:sso-hint-return-to";

/**
 * SSO-Hint per URL (Jan 15.09.2026, „ein Viewer"): das Portal navigiert im
 * SELBEN Tab zum Viewer und haengt `?sso=1` an, wenn der Nutzer dort
 * eingeloggt ist. Der Receiver behandelt das wie einen postMessage-Hint
 * (once pro Tab, prompt=none). Muss zu portal/src/lib/viewer-navigation.ts
 * passen.
 */
export const SSO_QUERY_PARAM = "sso";

/** Liefert true, wenn die Query den SSO-Hint traegt. */
export function hatSsoQuery(search: string): boolean {
  try {
    return new URLSearchParams(search).get(SSO_QUERY_PARAM) === "1";
  } catch {
    return false;
  }
}

/** Dieselbe URL ohne den SSO-Parameter (fuer replaceState + return_to). */
export function ohneSsoQuery(href: string): string {
  try {
    const u = new URL(href);
    u.searchParams.delete(SSO_QUERY_PARAM);
    return u.toString();
  } catch {
    return href;
  }
}

export type SsoHintDecision = "ignore" | "ack-only" | "redirect";

/**
 * Pure Entscheidungslogik des Receivers — testbar ohne React/DOM.
 *
 * - "ignore":   Origin nicht in der Allowlist → gar nicht reagieren
 *               (auch kein Ack an fremde Origins).
 * - "ack-only": legitimer Sender, aber kein Redirect — Polling stoppen.
 * - "redirect": einmaliger top-level signinRedirect (prompt=none).
 */
export function decideSsoHint(input: {
  originAllowed: boolean;
  /** window.self === window.top — im iframe wird NIE redirected (Loop-Klasse vom 29.05.). */
  isTopWindow: boolean;
  /** SSO_ATTEMPTED_KEY bereits gesetzt (once pro Tab). */
  attempted: boolean;
  isAuthenticated: boolean;
}): SsoHintDecision {
  if (!input.originAllowed) return "ignore";
  if (!input.isTopWindow) return "ack-only";
  if (input.attempted) return "ack-only";
  if (input.isAuthenticated) return "ack-only";
  return "redirect";
}

/** OAuth-Fehlercodes, die bei prompt=none ein erwarteter Silent-Fail sind. */
export const SILENT_SSO_FAIL_ERRORS = new Set([
  "login_required",
  "interaction_required",
  "consent_required",
  "account_selection_required",
]);

// ---------------------------------------------------------------------------
// Unsichtbarer Sprung (Benedikt 2026-09-18, "Weg 1 + 2"):
// Der stille prompt=none-Sprung kehrt nicht mehr ueber /oidc-callback zurueck,
// sondern direkt auf die Scrolly-Adresse (redirect_uri = aktuelle Seite ohne
// sso-Parameter). react-oidc-context erkennt code/state bzw. error/state auf
// JEDER Seite und tauscht das Token dort; onSigninCallback raeumt die Adresse.
// Beim Silent-Fail (login_required) bleibt der Fehler in der Adresse stehen,
// deshalb stripAuthParams() im Receiver. Waehrend des Roundtrips haelt ein
// Inline-Script im SSR-<head> die Seite unsichtbar (data-sso-pending), damit
// weder Text noch Doppelbild zu sehen sind; der Receiver deckt sie wieder auf.
// Voraussetzung: der Keycloak-Client scrolly-public erlaubt `<viewer>/*` als
// Redirect-URI (provision-keycloak.yml).
// ---------------------------------------------------------------------------

/** html-Attribut, das die Seite waehrend des stillen Sprungs verbirgt. */
export const SSO_PENDING_ATTR = "data-sso-pending";

const AUTH_PARAMS = ["code", "state", "session_state", "iss", "error", "error_description"];

/** Redirect-URI fuer den stillen Sprung: aktuelle Seite ohne sso-Parameter und ohne Hash. */
export function silentRedirectUri(href: string): string {
  try {
    const u = new URL(href);
    u.searchParams.delete(SSO_QUERY_PARAM);
    for (const k of AUTH_PARAMS) u.searchParams.delete(k);
    u.hash = "";
    return u.toString();
  } catch {
    return href;
  }
}

/** Dieselbe Adresse ohne OIDC-Rueckgabe-Parameter (nach Erfolg oder Silent-Fail). */
export function stripAuthParams(href: string): string {
  try {
    const u = new URL(href);
    for (const k of AUTH_PARAMS) u.searchParams.delete(k);
    u.searchParams.delete(SSO_QUERY_PARAM);
    return u.toString();
  } catch {
    return href;
  }
}

/** Traegt die Adresse OIDC-Rueckgabe-Parameter (code/state oder error/state)? */
export function hatAuthParams(search: string): boolean {
  try {
    const p = new URLSearchParams(search);
    return !!((p.get("code") || p.get("error")) && p.get("state"));
  } catch {
    return false;
  }
}

/** Seite nach dem stillen Sprung wieder aufdecken (Gegenstueck zum Inline-Script). */
export function revealPage(): void {
  if (typeof document === "undefined") return;
  document.documentElement.removeAttribute(SSO_PENDING_ATTR);
}

/**
 * Inline-Script fuer den SSR-<head> (als String, damit es VOR dem ersten
 * Paint laeuft): verbirgt die Seite, wenn ein stiller Sprung bevorsteht
 * (?sso=1, noch nicht versucht, top-level) oder gerade zurueckkommt
 * (code/error + state). Sicherheitsnetz: nach 6 s auf jeden Fall aufdecken.
 */
export const SSO_PENDING_INLINE_SCRIPT =
  "(function(){try{var p=new URLSearchParams(location.search);" +
  "var ret=!!((p.get('code')||p.get('error'))&&p.get('state'));" +
  "var hint=p.get('sso')==='1';if(!hint&&!ret)return;" +
  "if(window.self!==window.top)return;" +
  "if(hint&&sessionStorage.getItem('" + SSO_ATTEMPTED_KEY + "')==='1')return;" +
  "document.documentElement.setAttribute('" + SSO_PENDING_ATTR + "','1');" +
  "setTimeout(function(){document.documentElement.removeAttribute('" + SSO_PENDING_ATTR + "')},6000);" +
  "}catch(e){}})();";
